import { useEffect, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'

import { api, ApiError, type Profile } from '../api'
import { loginPath, logout } from '../auth'
import { ConnectedApps } from '../components/ConnectedApps'
import { ProblemBanner } from '../components/Problem'

const PAID_TIERS = [
  { tier: 'starter', name: 'Starter', price: '£3.00' },
  { tier: 'standard', name: 'Standard', price: '£7.50' },
  { tier: 'unlimited', name: 'Unlimited', price: '£15.00' },
]

function tierName(tier: string): string {
  return PAID_TIERS.find((option) => option.tier === tier)?.name ?? tier
}

/** How long to keep polling for Stripe's webhook before giving up (spec §5.3 discussion:
 *  delivery is asynchronous, so the redirect back from Checkout can arrive before it). */
const CHECKOUT_CONFIRM_TIMEOUT_MS = 20_000
const CHECKOUT_CONFIRM_POLL_MS = 1_500

/** What Stripe Checkout's redirect back here (`?checkout=`) means for the user. */
type CheckoutOutcome =
  | { state: 'confirming'; tier: string }
  | { state: 'confirmed'; tier: string }
  | { state: 'delayed'; tier: string | null }
  | { state: 'cancelled' }

function readCheckoutOutcome(parameters: URLSearchParams): CheckoutOutcome | null {
  const checkout = parameters.get('checkout')
  const tier = parameters.get('tier')
  if (checkout === 'cancelled') return { state: 'cancelled' }
  if (checkout !== 'success') return null
  // Without the target tier there is nothing to poll for, so say it is on its way.
  return tier ? { state: 'confirming', tier } : { state: 'delayed', tier: null }
}

/** Set while the user is away re-entering their password to delete the account. */
const PENDING_DELETE_KEY = 'flightdeck.pendingDelete'

function needsReauthentication(error: unknown): boolean {
  return error instanceof ApiError && error.problem.type.endsWith('/reauthentication-required')
}

export default function Account({ onSignedOut }: { onSignedOut: () => void }) {
  const [profile, setProfile] = useState<Profile | null>(null)
  const [error, setError] = useState<unknown>(null)
  const [parameters] = useSearchParams()
  const navigate = useNavigate()
  // Read once: the params are cleared as soon as the outcome is settled, and that must
  // not reset what the banner says.
  const [initialCheckout] = useState(() => readCheckoutOutcome(parameters))
  const [checkout, setCheckout] = useState(initialCheckout)
  const [resumingDelete] = useState(() => {
    const pending = sessionStorage.getItem(PENDING_DELETE_KEY) !== null
    sessionStorage.removeItem(PENDING_DELETE_KEY)
    return pending
  })

  const deleteAccount = () => {
    api
      .deleteAccount()
      .then(() => {
        onSignedOut()
        window.location.assign('/')
      })
      .catch((failure: unknown) => {
        if (!needsReauthentication(failure)) {
          setError(failure)
          return
        }
        // Deletion needs a password entered in the last few minutes. Come back here
        // afterwards and ask once more, rather than deleting on arrival.
        sessionStorage.setItem(PENDING_DELETE_KEY, '1')
        navigate(loginPath({ returnTo: '/account', reauth: true }))
      })
  }

  useEffect(() => {
    // Never cached: archiving frees quota without any user action, so a stale count
    // would show a limit the user has not actually reached (spec §10.2).
    //
    // After a successful Checkout, Stripe delivers the checkout.session.completed
    // webhook to us asynchronously, so the profile can still show the pre-upgrade tier
    // for a few seconds after Checkout redirects back here. Keep fetching until it
    // catches up (or we give up), then drop the query params so a reload of this page
    // doesn't re-trigger the banner.
    const awaitedTier = initialCheckout?.state === 'confirming' ? initialCheckout.tier : null
    const deadline = Date.now() + CHECKOUT_CONFIRM_TIMEOUT_MS
    let cancelled = false
    let timer: ReturnType<typeof setTimeout> | undefined

    const settle = (outcome: CheckoutOutcome) => {
      setCheckout(outcome)
      navigate('/account', { replace: true })
    }

    const load = () => {
      api
        .me()
        .then((latest) => {
          if (cancelled) return
          setProfile(latest)
          if (!awaitedTier) return
          if (latest.tier === awaitedTier) {
            settle({ state: 'confirmed', tier: awaitedTier })
          } else if (Date.now() >= deadline) {
            settle({ state: 'delayed', tier: awaitedTier })
          } else {
            timer = setTimeout(load, CHECKOUT_CONFIRM_POLL_MS)
          }
        })
        .catch((failure: unknown) => {
          if (cancelled) return
          setError(failure)
          if (awaitedTier) settle({ state: 'delayed', tier: awaitedTier })
        })
    }

    load()
    if (initialCheckout && !awaitedTier) navigate('/account', { replace: true })

    return () => {
      cancelled = true
      clearTimeout(timer)
    }
  }, [initialCheckout, navigate])

  // Set synchronously by the click, so a fast double click cannot start two requests
  // before React has re-rendered the buttons as disabled. Held until the redirect
  // happens or the request fails: the page unloads on success.
  const redirecting = useRef(false)
  const [pending, setPending] = useState(false)

  const goTo = (start: () => Promise<{ url: string }>) => {
    if (redirecting.current) return
    redirecting.current = true
    setPending(true)
    start()
      .then(({ url }) => window.location.assign(url))
      .catch((failure: unknown) => {
        redirecting.current = false
        setPending(false)
        setError(failure)
      })
  }

  return (
    <>
      <h1>Account</h1>
      <ProblemBanner error={error} />
      {profile ? (
        <>
          <CheckoutBanner outcome={checkout} onDismiss={() => setCheckout(null)} />
          <p className="muted">
            You are on the <strong>{tierName(profile.tier)}</strong> plan
            {profile.subscriptionStatus ? ` (${profile.subscriptionStatus})` : ''}.
          </p>

          <table>
            <tbody>
              <tr>
                <th>Decks</th>
                <td>
                  {profile.usage.decks} of {profile.usage.deckLimit}
                </td>
              </tr>
              <tr>
                <th>Images</th>
                <td>
                  {profile.usage.images} of {profile.usage.imageLimit}
                </td>
              </tr>
              <tr>
                <th>Image storage</th>
                <td>
                  {Math.round(profile.usage.imageBytes / 1048576)} MB of{' '}
                  {Math.round(profile.usage.imageByteLimit / 1048576)} MB
                </td>
              </tr>
            </tbody>
          </table>

          <h2>Plan</h2>
          {checkout?.state === 'confirming' ? (
            <p className="muted">Plan changes are available once your subscription is confirmed.</p>
          ) : (
            <>
              <div className="plans">
                {PAID_TIERS.map((option) => {
                  const current = option.tier === profile.tier
                  return (
                    <div
                      key={option.tier}
                      className={current ? 'plan current' : 'plan'}
                      aria-current={current ? 'true' : undefined}
                    >
                      <span className="plan-name">{option.name}</span>
                      <span className="plan-price">{option.price} / month</span>
                      {current ? (
                        <span className="plan-badge">Current plan</span>
                      ) : (
                        <button
                          aria-label={`Switch to ${option.name}`}
                          disabled={pending}
                          // A second Checkout would start a second subscription, so a
                          // subscribed user changes plan in the Billing Portal (issue #70).
                          onClick={() =>
                            goTo(() =>
                              profile.tier === 'free' ? api.checkout(option.tier) : api.portal(),
                            )
                          }
                        >
                          {pending ? 'Redirecting…' : 'Switch'}
                        </button>
                      )}
                    </div>
                  )
                })}
              </div>
              {profile.tier !== 'free' ? (
                <div className="row">
                  <button
                    className="primary"
                    disabled={pending}
                    onClick={() => goTo(() => api.portal())}
                  >
                    {pending ? 'Redirecting…' : 'Manage subscription'}
                  </button>
                </div>
              ) : null}
            </>
          )}
          <p className="muted">
            Moving to a smaller plan never deletes a deck. Decks beyond the new limit are locked and
            stay visible; they keep counting towards your allowance until you delete them, and can
            be unlocked once you are inside it.
          </p>

          <ConnectedApps />

          <h2>Session</h2>
          <div className="row">
            <button
              onClick={() => {
                logout().catch(() => onSignedOut())
              }}
            >
              Log out
            </button>
          </div>

          <h2>Delete account</h2>
          <p className="muted">
            This cancels any subscription and erases every deck, image and score. It cannot be
            undone. You will be asked for your password first.
          </p>
          {resumingDelete ? (
            <p className="problem">
              You have signed in again. Delete your account now? This cannot be undone.
            </p>
          ) : null}
          <button
            className={resumingDelete ? 'danger confirm' : 'danger'}
            onClick={() => {
              if (
                !resumingDelete &&
                !confirm('Delete your account and everything in it? This cannot be undone.')
              ) {
                return
              }
              deleteAccount()
            }}
          >
            {resumingDelete ? 'Yes, delete my account' : 'Delete my account'}
          </button>
        </>
      ) : (
        <p className="muted">Loading…</p>
      )}
    </>
  )
}

function CheckoutBanner({
  outcome,
  onDismiss,
}: {
  outcome: CheckoutOutcome | null
  onDismiss: () => void
}) {
  if (!outcome) return null

  if (outcome.state === 'confirming') {
    return (
      <div className="notice" role="status">
        <strong>Finalizing your {tierName(outcome.tier)} subscription…</strong>
        <p className="muted">This usually takes a few seconds.</p>
      </div>
    )
  }

  const dismiss = (
    <button className="link" onClick={onDismiss}>
      Dismiss
    </button>
  )

  if (outcome.state === 'confirmed') {
    return (
      <div className="notice good" role="status">
        <strong>Subscription updated.</strong>
        <p>
          You are now on the {tierName(outcome.tier)} plan. Your new limits apply straight away.{' '}
          {dismiss}
        </p>
      </div>
    )
  }

  if (outcome.state === 'delayed') {
    const plan = outcome.tier ? `${tierName(outcome.tier)} subscription` : 'subscription'
    return (
      <div className="notice warn" role="status">
        <strong>Your {plan} is still being confirmed.</strong>
        <p>
          Some payment methods take longer to clear. Your plan updates automatically once Stripe
          confirms the payment — refresh this page in a few minutes. There is no need to check out
          again. {dismiss}
        </p>
      </div>
    )
  }

  return (
    <div className="notice" role="status">
      <strong>Checkout cancelled.</strong>
      <p>You have not been charged and your plan has not changed. {dismiss}</p>
    </div>
  )
}
